import {
  DrugReferenceFgNdc,
  DrugReferenceFgNdcPayload
} from 'Interfaces/ReferenceInterfaces/DrugReferenceFgNdcInterface';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { drugReferenceFgNdcServices } from 'services/ReferenceScreenServices/drugReferenceFgNdcServices';

export class DrugReferenceFgNdcStore {
  drugReferenceFgNdcs: DrugReferenceFgNdc[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  searchQuery: string = '';
  selectedDrugReferenceFgNdc: DrugReferenceFgNdc | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  fetchDrugReferenceFgNdcs = async (page = 1, pageSize = 10000, search?: string) => {
    this.isLoading = true;
    try {
      const response = await drugReferenceFgNdcServices.getAllDrugReferenceFgNdcs(
        page,
        pageSize,
        search
      );
      runInAction(() => {
        this.drugReferenceFgNdcs = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
        if (search !== undefined) {
          this.searchQuery = search;
        }
      });
    } catch (error) {
      toast.error('Error fetching drug references');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  pollDrugReferenceFgNdcs = async (page = 1, pageSize = 10000, search?: string) => {
    this.isPolling = true;
    try {
      const response = await drugReferenceFgNdcServices.getAllDrugReferenceFgNdcs(
        page,
        pageSize,
        search
      );
      runInAction(() => {
        if (JSON.stringify(this.drugReferenceFgNdcs) !== JSON.stringify(response.data)) {
          this.drugReferenceFgNdcs = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
          if (search !== undefined) {
            this.searchQuery = search;
          }
        }
      });
    } catch (error) {
      if (this.environment === 'development') {
        console.error('Error polling drug references:', error);
      }
      toast.error('Error polling drug reference Fg NDCs!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  refreshData = async (page = 1, pageSize = 10000, search?: string) => {
    await this.fetchDrugReferenceFgNdcs(page, pageSize, search);
  };

  createDrugReferenceFgNdc = async (
    drugReferenceFgNdcData: DrugReferenceFgNdcPayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response =
        await drugReferenceFgNdcServices.createDrugReferenceFgNdc(drugReferenceFgNdcData);
      if (response.status) {
        toast.success('Drug reference created successfully');
        await this.fetchDrugReferenceFgNdcs(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to create drug reference');
        return false;
      }
    } catch (error) {
      toast.error('Error creating drug reference');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateDrugReferenceFgNdc = async (
    id: number,
    drugReferenceFgNdcData: DrugReferenceFgNdcPayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await drugReferenceFgNdcServices.updateDrugReferenceFgNdc(
        id,
        drugReferenceFgNdcData
      );
      if (response.status) {
        toast.success('Drug reference updated successfully');
        await this.fetchDrugReferenceFgNdcs(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to update drug reference');
        return false;
      }
    } catch (error) {
      toast.error('Error updating drug reference');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteDrugReferenceFgNdc = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await drugReferenceFgNdcServices.deleteDrugReferenceFgNdc(id);
      if (response.status) {
        toast.success('Drug reference deleted successfully');

        const isLastItemOnPage = this.drugReferenceFgNdcs.length === 1;
        const isLastPage = this.pagination.currentPage === this.pagination.totalPages;

        if (isLastItemOnPage && !isLastPage && this.pagination.currentPage > 1) {
          await this.fetchDrugReferenceFgNdcs(
            this.pagination.currentPage - 1,
            10,
            this.searchQuery || undefined
          );
        } else {
          await this.fetchDrugReferenceFgNdcs(
            this.pagination.currentPage,
            10,
            this.searchQuery || undefined
          );
        }
      } else {
        toast.error(response.message || 'Failed to delete drug reference');
      }
    } catch (error) {
      toast.error('Error deleting drug reference');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedDrugReferenceFgNdc = (drugReference: DrugReferenceFgNdc | null) => {
    this.selectedDrugReferenceFgNdc = drugReference;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };
}

export const drugReferenceFgNdcStore = new DrugReferenceFgNdcStore();
